<template>
  <div class="email-content-area">
    <h4>
      <i class="fas fa-envelope"></i> Conteúdo dos e-mails
      <span class="badge badge-danger">{{ emails ? emails.length : 0 }}</span>
    </h4>

    <div class="accordion" :id="'emailsAccordion-' + _uid">
      <div class="card email-card-accordion" v-for="(email, index) in emails" :key="email.id">
        <div class="card-header" :id="'heading-' + _uid + '-' + index">
          <h2 class="mb-0">
            <button
              class="btn btn-outline-dark collapsed w-100 text-left d-flex justify-content-between align-items-center"
              type="button"
              data-toggle="collapse"
              :data-target="'#collapse-' + _uid + '-' + index"
              :aria-expanded="false"
              :aria-controls="'collapse-' + _uid + '-' + index"
            >
              <div>
                <strong>
                  {{ primaryEmailId == email.id ? `⭐ ${email.subject}` : email.subject }}
                </strong>
                <small class="text-muted ml-2">{{ emailSubTitle(email) }}</small>
                <span v-if="isNoreply(email)" class="badge badge-secondary ml-2" title="Enviado por uma caixa no-reply">
                  No-reply
                </span>
              </div>
              <i class="fas fa-chevron-down"></i>
            </button>
          </h2>
        </div>

        <div
          :id="'collapse-' + _uid + '-' + index"
          class="collapse"
          :aria-labelledby="'heading-' + _uid + '-' + index"
        >
          <div class="card-body">
            <div class="email-content-header">
              <h5>{{ email.subject }}</h5>
              <p>
                <strong>{{ email.is_received ? "Recebido" : "Enviado" }} em: </strong>
                {{ formatDate(email.created_at) }}
              </p>
              <p><strong>De: </strong>{{ handleEmailsArray(email.from_email) }}</p>
              <p><strong>Para: </strong>{{ handleEmailsArray(email.to_email) }}</p>
              <p v-if="email.copy_email && email.copy_email.length">
                <strong>Cópia: </strong>{{ handleEmailsArray(email.copy_email) }}
              </p>
            </div>

            <div class="email-content-body">
              <div style="overflow-x: auto; max-width: 985px;" v-html="parseBody(email.body)"></div>

              <div class="composer-attachments" v-if="email.attachments && email.attachments.length > 0">
                <h5>Anexos:</h5>
                <ul>
                  <li v-for="(attachment, attachIndex) in email.attachments" :key="attachIndex">
                    <span>
                      <i :class="getFileIcon(attachment.filename)"></i>
                      <span>{{ textSizeLimit(attachment.filename, 22) }}</span>
                      <small class="attachment-size">({{ formatFileSize(attachment.size) }})</small>
                    </span>
                    <a 
                      :href="generateDownloadURL(attachment.filename, email.id)"
                      :download="attachment.filename"
                      class="download-attachment-btn"
                      title="Download anexo"
                    >
                      <i class="fas fa-download"></i>
                    </a>
                  </li>
                </ul>
              </div>
            </div>

            <div v-if="showReplyButtons" style="display: flex; gap: 8px; margin-top: 10px;">
              <button class="btn btn-warning" @click="openEmailEditor(email)">
                <i class="fas fa-reply"></i> Responder E-mail
              </button>
              <button class="btn btn-success" @click="openEmailEditor(email, true)">
                <i class="fas fa-share"></i> Encaminhar E-mail
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <b-modal
      :id="'email-editor-modal-' + _uid"
      size="xl"
      hide-footer
      hide-header
      no-enforce-focus
      scrollable
    >
      <EmailEditor
        :visible="showEmailEditor"
        @close="closeEditor"
        :responseData="replyEmail"
        @emailSent="onEmailSent"
        :emailGroups="emailGroups"
        :solicitationData="solicitationData"
      />
    </b-modal>
  </div>
</template>

<script>
import EmailEditor from '../emails/EmailEditor.vue';
import { formatDate, getFileIcon, formatFileSize, textSizeLimit } from '../../helpers/utils';

export default {
  name: 'TicketEmailsViewer',
  components: { EmailEditor },
  props: {
      emailGroups: {
          type: Array,
          default: () => []
      },
      emails: {
          type: Array,
          default: () => [],
      },
      primaryEmailId: {
          type: Number,
          default: null,
      },
      ticketProtocol: {
          type: String,
          default: null,
      },
      ticketId: {
          type: Number,
          default: null,
      },
      requestId: {
          type: String,
          default: null,
      },
      showReplyButtons: {
          type: Boolean,
          default: true,
      },
      solicitationData: {
          type: Object,
          default: () => ({})
      },
  },
  emits: ['emailSent'],
  data: () => ({
      showEmailEditor: false,
      replyEmail: null,
      noreplyAddresses: [],
  }),
  created() {
      this.getNoreplyAddresses();
  },
  methods: {
      formatDate,
      getFileIcon,
      formatFileSize,
      textSizeLimit,

      emailSubTitle(email) {
          return email.is_received
              ? `Recebido em: ${this.formatDate(email.created_at)}`
              : `Enviado em: ${this.formatDate(email.created_at)}`;
      },

      getNoreplyAddresses() {
          axios.get('/emails/config/noreply')
              .then(({ data }) => {
                  this.noreplyAddresses = (Array.isArray(data) ? data : [])
                      .map(config => (config.email_address || '').toLowerCase());
              })
              .catch(error => console.error('Erro ao buscar caixas no-reply:', error));
      },

      isNoreply(email) {
          if (!email || email.is_received) return false;
          let from = email.email_config || email.from_email || '';
          if (Array.isArray(from)) from = from[0] || '';
          return this.noreplyAddresses.includes(String(from).toLowerCase());
      },

      handleEmailsArray(emails) {
          if (!emails) return '';
          if (!Array.isArray(emails)) return emails;
          return emails.join('; ');
      },

      generateDownloadURL(filename, emailId) {
          if (!filename || !emailId) return '';
          return `${location.origin}/emails/${emailId}/attachment/${filename}`;
      },

      openEmailEditor(email, isForward = false) {
          if (!email?.ticket_id && this.ticketId) {
              email.ticket_id = this.ticketId;
          }

          if (isForward) {
              this.replyEmail = {
                  id: email.id,
                  in_reply_to: null,
                  protocol: this.ticketProtocol,
                  subject_email: `Fwd: ${email.subject}`,
                  from_email: email.from_email,
                  to_email: [],
                  copy_email: [],
                  ticket_id: email.ticket_id,
                  request_id: this.requestId ?? null,
                  is_received: false,
                  is_forward: true,
                  forwarded_email: email,
                  forwarded_attachments: (email.attachments ?? []).map(att => ({
                      filename: att.filename,
                      email_id: email.id,
                  })),
                  references: null,
                  thread_index: null,
              };
          } else {
              this.replyEmail = {
                  id: email.id,
                  in_reply_to: email.message_id,
                  protocol: this.ticketProtocol,
                  subject_email: email.subject,
                  from_email: email.from_email,
                  to_email: email.to_email,
                  copy_email: email.copy_email,
                  ticket_id: email.ticket_id,
                  is_received: email.is_received,
                  reply_email: email,
                  references: email.references,
                  thread_index: email.thread_index,
              };
          }

          this.showEmailEditor = true;
          this.$nextTick(() => {
              this.$bvModal.show('email-editor-modal-' + this._uid);
          });
      },

      closeEditor() {
          this.showEmailEditor = false;
          this.replyEmail = null;
          this.$bvModal.hide('email-editor-modal-' + this._uid);
      },

      onEmailSent(data) {
          this.closeEditor();
          this.$emit('emailSent', data);
      },
      parseBody(body){
        return body.replaceAll('ura-n1.tbforte.tbf', 'ura-n2.tbforte.tbf')
      }
  },
};
</script>

<style scoped>
.email-content-area {
  background: white;
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 20px;
  margin-bottom: 20px;
}
.email-content-area > h4 { margin-bottom: 15px; color: #333; }
.email-content-body { border-bottom: 1px solid #e6e6e6; margin: 10px 0; }
.email-content-header { border-bottom: 1px solid #c2c2c2; padding-bottom: 10px; }
.email-card-accordion { margin: 10px 0; }

.composer-attachments { margin-top: 15px; padding-top: 15px; border-top: 1px solid #eee; }
.composer-attachments > h5 { font-size: 13px; margin: 0 0 10px 0; color: #333; }
.composer-attachments > ul {
  list-style: none; padding: 0; margin: 0;
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px;
}
.composer-attachments > ul > li {
  display: flex; flex-direction: row; justify-content: space-between;
  align-items: center; background: #f8f9fa; border-radius: 4px;
  font-size: 12px; border: 1px solid #e9ecef;
  transition: background-color 0.2s; text-align: center;
}
.composer-attachments > ul > li:hover { background: #e9ecef; }
.composer-attachments > ul > li > span {
  display: flex; flex-direction: row; align-items: center;
  gap: 6px; flex-grow: 1; justify-content: center;
}
.composer-attachments > ul > li > span > i { color: #2e8b8b; font-size: 18px; }
.attachment-size { color: #6c757d; font-size: 10px; }

.download-attachment-btn {
  background: none; border: none; color: #2e8b8b;
  cursor: pointer; padding: 4px 8px; border-radius: 3px;
  text-decoration: none; transition: all 0.2s; font-size: 12px;
}
.download-attachment-btn:hover { background-color: #2e8b8b; color: white; text-decoration: none; }

.btn { padding: 8px 15px; border: 1px solid; border-radius: 4px !important; cursor: pointer; font-size: 11px; font-weight: bold; transition: all 0.3s; }
.btn-warning { background-color: #ffc107; color: #333; border-color: #ffc107; }
.btn-success { background-color: #28a745; color: white; border-color: #28a745; }
.btn-success:hover { background-color: #218838; }

@media (max-width: 768px) { .composer-attachments > ul { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 480px) { .composer-attachments > ul { grid-template-columns: 1fr; } }
</style>